import { getFunctions, httpsCallable } from "firebase/functions";

function designBoardFunctions() {
  return getFunctions(undefined, "us-central1");
}

function safeStr(value) {
  return String(value ?? "").trim();
}

/**
 * Maps Firebase callable failures to user-facing copy; logs full detail in development.
 */
export function formatDesignBoardUploadCallableError(error, phase = "prepare") {
  const code = safeStr(error?.code);
  const message = safeStr(error?.message);
  const details = safeStr(error?.details);

  if (process.env.NODE_ENV !== "production") {
    console.error("[design-board-upload]", phase, { code, message, details, error });
  }

  if (code === "functions/unauthenticated") {
    return "Sign in again to upload files.";
  }
  if (code === "functions/permission-denied") {
    return "You do not have permission to upload files for this board.";
  }
  if (code === "functions/not-found") {
    return "Upload could not start because the board or upload service was not found.";
  }
  if (code === "functions/failed-precondition") {
    return message || "This board cannot accept uploads right now.";
  }
  if (code === "functions/invalid-argument") {
    return message || "Choose a supported image or PDF.";
  }
  if (code === "functions/unavailable" || code === "functions/deadline-exceeded") {
    return "Upload service is temporarily unavailable. Try again in a moment.";
  }
  if (
    code === "functions/internal"
    || message.toLowerCase().includes("internal")
    || message.toLowerCase().includes("failed to fetch")
    || message.toLowerCase().includes("network")
  ) {
    if (process.env.REACT_APP_USE_FIREBASE_EMULATORS === "true") {
      return "Upload could not reach the local functions emulator. Restart the DB-2 emulator stack so design board upload callables are loaded, then try again.";
    }
    return "Upload could not be prepared. Try again or contact your project team if this continues.";
  }
  if (message) return message;
  return phase === "finalize"
    ? "Upload finished but could not be saved. Try again."
    : "Upload could not be prepared. Try again.";
}

async function invokeCallable(name, payload) {
  const callable = httpsCallable(designBoardFunctions(), name);
  const response = await callable(payload);
  return response?.data || {};
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error("Could not read file."));
    reader.readAsDataURL(file);
  });
}

export async function emulatorTrustedProjectDesignBoardFileUpload(payload = {}) {
  try {
    return await invokeCallable("emulatorTrustedProjectDesignBoardFileUpload", payload);
  } catch (error) {
    const err = new Error(formatDesignBoardUploadCallableError(error, "upload"));
    err.cause = error;
    throw err;
  }
}

/** Callable request bodies are capped (~10MB); base64 inflates size — stay under this in emulator mode. */
const EMULATOR_TRUSTED_UPLOAD_MAX_BYTES = 7 * 1024 * 1024;

export async function uploadProjectDesignBoardFileForCurrentRuntime({
  projectId,
  boardId,
  file,
  clientMutationId,
}) {
  if (process.env.REACT_APP_USE_FIREBASE_EMULATORS === "true") {
    if (file?.size > EMULATOR_TRUSTED_UPLOAD_MAX_BYTES) {
      throw new Error(
        "This file is too large for local emulator upload. Use a smaller file or Choose existing for local testing."
      );
    }
    const fileBase64 = await fileToBase64(file);
    const result = await emulatorTrustedProjectDesignBoardFileUpload({
      projectId,
      boardId,
      fileName: file.name,
      contentType: file.type || "application/octet-stream",
      sizeBytes: file.size,
      fileBase64,
      clientMutationId,
    });
    return { fileId: result?.fileId, contentType: file.type };
  }
  const begin = await beginProjectDesignBoardFileUpload({
    projectId,
    boardId,
    fileName: file.name,
    contentType: file.type || "application/octet-stream",
    sizeBytes: file.size,
    clientMutationId,
  });
  if (!begin?.uploadUrl || !begin?.fileId) {
    throw new Error("Upload could not be prepared.");
  }
  const put = await fetch(begin.uploadUrl, {
    method: begin.uploadMethod || "PUT",
    headers: { "Content-Type": file.type || begin.contentType || "application/octet-stream" },
    body: file,
  });
  if (!put.ok) {
    throw new Error(`Upload failed (${put.status}).`);
  }
  await finalizeProjectDesignBoardFileUpload({
    projectId,
    boardId,
    fileId: begin.fileId,
    fileName: file.name,
    contentType: file.type || begin.contentType,
    sizeBytes: file.size,
    clientMutationId,
  });
  return { fileId: begin.fileId, contentType: file.type || begin.contentType };
}

export async function beginProjectDesignBoardFileUpload(payload = {}) {
  try {
    return await invokeCallable("beginProjectDesignBoardFileUpload", payload);
  } catch (error) {
    const err = new Error(formatDesignBoardUploadCallableError(error, "prepare"));
    err.cause = error;
    throw err;
  }
}

export async function finalizeProjectDesignBoardFileUpload(payload = {}) {
  try {
    return await invokeCallable("finalizeProjectDesignBoardFileUpload", payload);
  } catch (error) {
    const err = new Error(formatDesignBoardUploadCallableError(error, "finalize"));
    err.cause = error;
    throw err;
  }
}

export async function getProjectDesignBoardFileReadUrl(payload = {}) {
  try {
    return await invokeCallable("getProjectDesignBoardFileReadUrl", payload);
  } catch (error) {
    const err = new Error(formatDesignBoardUploadCallableError(error, "read"));
    err.cause = error;
    throw err;
  }
}
